<template>
  <div>
    <!-- Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-[#0000009e] flex items-center justify-center z-50" @click="closeModal">
        <div
          class="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-all duration-300 ease-in-out" @click.stop>
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-bold">
              {{ mode === 'create' ? 'Nouvel agent' : 'Modifier l\'agent' }}
            </h2>
            <button class="text-gray-500 hover:text-gray-700" @click="closeModal">
              <XIcon class="h-6 w-6" />
            </button>
          </div>

          <!-- Formulaire -->
          <div class="space-y-4">
            <!-- Toggle esthétique pour le type d'agent -->
            <div class="flex items-center gap-2 mb-4">
              <button
                :class="['px-4 py-2 rounded-l', !agentData.is_legal_entity ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700']"
                type="button" @click="agentData.is_legal_entity = false">Personne physique</button>
              <button
                :class="['px-4 py-2 rounded-r', agentData.is_legal_entity ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700']"
                type="button" @click="agentData.is_legal_entity = true">Personne morale</button>
            </div>

            <!-- Personne physique -->
            <div v-if="!agentData.is_legal_entity">
              <!-- Recherche AuréHAL -->
              <div class="mb-4 relative">
                <label class="block text-sm font-medium text-gray-700">Recherche dans AuréHAL</label>
                <div class="relative">
                  <input ref="halSearchInput" v-model="halSearchQuery"
                    class="mt-1 block w-full pr-8 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Rechercher une personne dans la base AuréHAL..." @input="handleHalSearchInput" />
                  <button v-if="halSearchQuery" class="absolute inset-y-0 right-0 pr-3 flex items-center text-red-600"
                    @click="resetHalSearch">
                    <XIcon class="h-4 w-4" />
                  </button>
                </div>
                <!-- Suggestions HAL -->
                <div v-if="(showSuggestions && halSuggestions.length > 0) || isLoading"
                  class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 min-h-60 overflow-y-auto">
                  <div v-if="isLoading" class="p-3 text-center text-gray-600">
                    <div class="flex items-center justify-center">
                      <svg class="animate-spin h-5 w-5 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg"
                        fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                        </path>
                      </svg>
                      Recherche en cours...
                    </div>
                  </div>
                  <div v-else>
                    <div v-for="suggestion in halSuggestions" :key="suggestion.docid"
                      class="p-3 bg-white hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
                      @click="selectAuthor(suggestion)">
                      <div class="font-medium">
                        <span class="text-gray-500">{{ suggestion.firstName }}</span>
                        <span class="font-semibold"> {{ ' ' + suggestion.lastName }}</span>
                      </div>
                      <div class="text-sm text-gray-600">
                        Identifiant HAL: {{ suggestion.docid }}
                      </div>
                    </div>
                    <div v-if="halSuggestions.length === 0 && !isLoading" class="p-3 text-gray-500 text-center">
                      Aucun résultat, saisissez manuellement le prénom et le nom.
                    </div>
                    <div
                      class="p-3 bg-gray-100 border-t hover:bg-gray-100 cursor-pointer text-center text-blue-600 flex items-center justify-center"
                      @click="closeAuthorSuggestions">
                      <span class="flex items-center">
                        <XIcon class="h-4 w-4 mr-2 text-red-600" />
                        Saisir un autre nom
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Champs prénom et nom -->
              <div class="flex gap-4 mb-4">
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700">Prénom</label>
                  <input v-model="agentData.first_name"
                    class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Prénom" />
                </div>
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700">Nom de famille <span
                      class="text-red-500">*</span></label>
                  <input v-model="agentData.last_name"
                    class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Nom de famille" required />
                </div>
                <button v-if="agentData.first_name || agentData.last_name"
                  class="self-end mb-1 ml-2 text-red-600 hover:text-red-800" title="Effacer les champs nom/prénom"
                  @click="resetNameFields">
                  <XIcon class="h-4 w-4" />
                </button>
              </div>
              <div v-if="agentData.idref_id" class="mt-1 text-sm text-gray-500">
                ID HAL: {{ agentData.idref_id }}
              </div>

              <!-- Affiliation pour personne physique -->
              <div ref="affiliationContainer" class="relative">
                <label class="block text-sm font-medium text-gray-700">Affiliation</label>
                <div class="relative">
                  <input 
                    ref="affiliationInput"
                    v-model="agentData.affiliation"
                    class="mt-1 block w-full pr-20 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Saisissez ou sélectionnez une affiliation"
                    @click.stop="toggleAffiliationSuggestions"
                    @input="handleAffiliationInput" />
                  <div class="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
                    <button v-if="agentData.affiliation" class="text-red-600 hover:text-red-800"
                      @click.stop="resetAffiliation">
                      <XIcon class="h-4 w-4" />
                    </button>
                    <button v-if="selectedAuthorName" class="text-blue-600 hover:text-blue-800"
                      @click.stop="refreshAffiliations">
                      <RefreshCcwIcon class="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div v-if="agentData.affiliation_id" class="mt-1 text-sm text-gray-500">
                  ID Structure: {{ agentData.affiliation_id }}
                </div>

                <!-- Liste des affiliations pour personne physique -->
                <div v-if="isLoadingAffiliations" class="mt-2 text-center text-gray-600">
                  <div class="flex items-center justify-center">
                    <svg class="animate-spin h-5 w-5 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                      viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                      </path>
                    </svg>
                    Recherche des affiliations en cours...
                  </div>
                </div>

                <div v-if="showAffiliationSuggestions && affiliationSuggestions.length > 0"
                  class="absolute z-40 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 min-h-48 overflow-y-auto">
                  <div v-for="affiliation in affiliationSuggestions" :key="affiliation.id"
                    class="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
                    @click.stop="selectAffiliation(affiliation)">
                    <div class="flex items-center justify-between">
                      <span class="font-medium">
                        {{ affiliation.acronym ? `${affiliation.acronym} - ${affiliation.name}` : affiliation.name }}
                      </span>
                      <span :class="{
                        'text-green-600': affiliation.status === 'VALID',
                        'text-yellow-600': affiliation.status === 'OLD',
                        'text-blue-600': affiliation.status === 'INCOMING'
                      }" class="text-sm">
                        {{ affiliation.status }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700">Groupe</label>
                <input v-model="agentData.group"
                  class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Équipe ou groupe de travail" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700">Lien</label>
                <input v-model="agentData.agent_link"
                  class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="https://..." />
              </div>
            </div>

            <!-- Personne morale -->
            <div v-else>
              <!-- Recherche de structure pour personne morale -->
              <div class="mb-4 relative">
                <label class="block text-sm font-medium text-gray-700">Recherche de structure <span
                    class="text-red-500">*</span></label>
                <div class="relative">
                  <input ref="structureSearchInput" v-model="structureSearchQuery"
                    class="mt-1 block w-full pr-8 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Rechercher une structure dans la base HAL..." @input="handleStructureSearchInput" />
                  <button v-if="structureSearchQuery"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center text-red-600"
                    @click="resetStructureSearch">
                    <XIcon class="h-4 w-4" />
                  </button>
                </div>
                <!-- Suggestions de structures -->
                <div v-if="showStructureSuggestions && structureSuggestions.length > 0"
                  class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-y-auto">
                  <div v-for="structure in structureSuggestions" :key="structure.id"
                    class="p-3 bg-white hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
                    @click="selectStructure(structure)">
                    <div class="font-medium">{{ structure.name }}</div>
                    <div class="text-sm text-gray-600">ID: {{ structure.id }}</div>
                  </div>
                </div>
              </div>
              <div v-if="agentData.affiliation_id" class="mt-1 text-sm text-gray-500">
                ID Structure: {{ agentData.affiliation_id }}
              </div>

              <!-- Groupe pour personne morale -->
              <div>
                <label class="block text-sm font-medium text-gray-700">Groupe</label>
                <input v-model="agentData.group"
                  class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Équipe ou groupe de travail" />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700">Lien</label>
                <input v-model="agentData.agent_link"
                  class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="https://..." />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">Rôles</label>
              <p class="text-xs text-gray-500 mb-2">Rôles par défaut - peuvent être personnalisés dans chaque mémo</p>
              <TagsPicker v-model="agentData.roles" />
            </div>

            <!-- Champ Public/Privé -->
            <div class="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <input
                id="is_public"
                v-model="agentData.is_public"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label for="is_public" class="text-sm font-medium text-blue-800">
                Rendre public
              </label>
            </div>
            <p class="text-xs text-blue-600 mt-1">
              Un agent public sera visible par tous les utilisateurs de la plateforme.
            </p>

            <div class="flex justify-between mt-6">
              <div class="ml-auto">
                <button class="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600" @click="handleSubmit">
                  {{ mode === 'create' ? 'Créer l\'agent' : 'Mettre à jour l\'agent' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal de confirmation -->
    <ModalAreYouSure
      v-model="showConfirmModal"
      title="Modifications non sauvegardées"
      message="Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir fermer sans sauvegarder ?"
      confirm-text="Fermer sans sauvegarder"
      cancel-text="Continuer l'édition"
      @confirm="confirmClose"
      @cancel="cancelClose"
    />

    <!-- Notification -->
    <Notification ref="notification" />
  </div>
</template>

<script lang="ts">
import debounce from 'lodash/debounce';
import { RefreshCcwIcon, XIcon } from 'lucide-vue-next';
import type { PropType } from 'vue';
import { defineComponent, nextTick, reactive, ref, watch } from 'vue';
import { agents } from '../../api/agents';
import type { Agent, AgentCreate } from '../../api/types/entities/agent/types';
import Notification from '../Notification.vue';
import TagsPicker from '../common/TagsPicker.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

export interface HalAuthor {
  docid: string;
  label: string;
  firstName?: string;
  lastName?: string;
}

export interface HalAffiliation {
  id: string;
  name: string;
  acronym?: string;
  status: 'VALID' | 'OLD' | 'INCOMING';
}

export interface StructureSuggestion {
  id: string;
  name: string;
}

interface HalApiResponse {
  response: {
    docs: Array<{
      docid: string;
      label_s?: string;
      firstName_s?: string;
      lastName_s?: string;
    }>;
  };
}

interface StructureApiResponse {
  response: {
    docs: Array<{
      docid: string;
      label_s: string;
    }>;
  };
}

export default defineComponent({
  name: 'AgentCreator',
  components: {
    XIcon,
    TagsPicker,
    RefreshCcwIcon,
    Notification,
    ModalAreYouSure
  },
  props: {
    displayType: {
      type: String,
      default: 'card',
      validator: (value: string): boolean => ['card', 'button'].includes(value)
    },
    userId: {
      type: String,
      required: true
    },
    mode: {
      type: String as PropType<'create' | 'edit'>,
      default: 'create'
    },
    initialData: {
      type: Object as PropType<Agent>,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['agent-created', 'agent-updated', 'update:show', 'agent-exists'],
  setup(props, { emit }) {
    const showModal = ref(props.show);
    const halSearchQuery = ref('');
    const halSuggestions = ref<HalAuthor[]>([]);
    const showSuggestions = ref(false);
    const showConfirmModal = ref(false);
    const initialDataSnapshot = ref<AgentCreate | null>(null);
    const isLoading = ref(false);
    const affiliationSuggestions = ref<HalAffiliation[]>([]);
    const isLoadingAffiliations = ref(false);
    const selectedAuthorName = ref<{ firstName: string; lastName: string } | null>(null);
    const structureSearchQuery = ref('');
    const structureSuggestions = ref<StructureSuggestion[]>([]);
    const showStructureSuggestions = ref(false);
    const halSearchInput = ref<HTMLInputElement | null>(null);
    const structureSearchInput = ref<HTMLInputElement | null>(null);
    const affiliationInput = ref<HTMLInputElement | null>(null);
    const affiliationContainer = ref<HTMLDivElement | null>(null);
    const notification = ref<InstanceType<typeof Notification> | null>(null);
    const showAffiliationSuggestions = ref(false);

    const agentData = reactive<AgentCreate>({
      user_id: props.userId,
      last_name: '',
      first_name: '',
      is_legal_entity: false,
      idref_id: '',
      roles: '',
      group: '',
      affiliation: '',
      affiliation_id: '',
      agent_link: '',
      is_public: true,
    });

    // Ajout : watcher pour pré-remplir à chaque ouverture de la modale avec les données d'import, quel que soit le mode
    watch([
      () => props.initialData,
      showModal
    ], ([newData, modalVisible]) => {
      if (modalVisible && newData) {
        console.log('[AgentCreator] Pré-remplissage avec initialData:', JSON.parse(JSON.stringify(newData)));
        Object.assign(agentData, {
          user_id: newData.user_id || props.userId,
          uuid: newData.uuid || undefined, // Récupérer l'UUID s'il est fourni, sinon undefined
          first_name: newData.first_name || '',
          last_name: newData.last_name || '',
          affiliation: newData.affiliation || '',
          affiliation_id: newData.affiliation_id || '',
          agent_link: newData.agent_link || '',
          group: newData.group || '',
          is_legal_entity: newData.is_legal_entity ?? false,
          idref_id: newData.idref_id || ''
        });
        console.log('[AgentCreator] Formulaire après assignation :', JSON.parse(JSON.stringify(agentData)));
      } else {
        console.log('[AgentCreator] Watcher déclenché mais pas de données à appliquer (modalVisible =', modalVisible, ', initialData =', newData, ')');
      }
    }, { immediate: true });

    // Focus automatique sur le champ de recherche approprié
    const focusSearchInput = async (): Promise<void> => {
      await nextTick();
      if (agentData.is_legal_entity && structureSearchInput.value) {
        structureSearchInput.value.focus();
      } else if (!agentData.is_legal_entity && halSearchInput.value) {
        halSearchInput.value.focus();
      }
    };

    // Surveiller les changements de la prop show
    watch(() => props.show, async (newValue: boolean): Promise<void> => {
      showModal.value = newValue;
      if (newValue && props.mode === 'create') {
        await focusSearchInput();
      }
    });

    // Surveiller les changements de showModal
    watch(showModal, (newValue: boolean): void => {
      emit('update:show', newValue);
      // Réinitialiser le snapshot quand le modal se ferme
      if (!newValue) {
        initialDataSnapshot.value = null;
      }
    });

    // Gestionnaire de clic global pour fermer les suggestions d'affiliation
    const handleClickOutside = (event: Event): void => {
      const target = event.target as Element;
      if (affiliationContainer.value && !affiliationContainer.value.contains(target)) {
        showAffiliationSuggestions.value = false;
      }
    };

    // Ajouter/supprimer le gestionnaire de clic global
    watch(showModal, (newValue: boolean): void => {
      if (newValue) {
        document.addEventListener('click', handleClickOutside);
      } else {
        document.removeEventListener('click', handleClickOutside);
      }
    });

    // Surveiller les changements de type d'agent pour focus
    watch(() => agentData.is_legal_entity, async (): Promise<void> => {
      if (showModal.value && props.mode === 'create') {
        await focusSearchInput();
      }
    });

    // Initialiser les données si en mode édition
    watch(() => props.initialData, (newData: Agent | null): void => {
      if (newData && props.mode === 'edit') {
        Object.assign(agentData, {
          user_id: newData.user_id,
          last_name: newData.last_name || '',
          first_name: newData.first_name || '',
          is_legal_entity: newData.is_legal_entity || false,
          idref_id: newData.idref_id,
          roles: newData.roles || '',
          group: newData.group || '',
          affiliation: newData.affiliation || '',
          affiliation_id: newData.affiliation_id,
          agent_link: newData.agent_link || '',
          is_public: newData.is_public,
        });
        halSearchQuery.value = newData.last_name || '';
        structureSearchQuery.value = newData.affiliation || '';
        // Sauvegarder un snapshot des données initiales pour la comparaison
        initialDataSnapshot.value = JSON.parse(JSON.stringify(agentData));
      }
    }, { immediate: true });

    // Fonction de recherche HAL avec debounce
    const searchHal = debounce(async (query: string): Promise<void> => {
      if (query.length < 3) {
        halSuggestions.value = [];
        showSuggestions.value = false;
        isLoading.value = false;
        return;
      }

      isLoading.value = true;
      showSuggestions.value = true;

      try {
        const searchTerm = query.endsWith('*') ? query : `${query}*`;
        const response = await fetch(`https://api.archives-ouvertes.fr/ref/author/?q=${encodeURIComponent(searchTerm)}&wt=json&rows=20&fl=label_s,firstName_s,lastName_s,docid`);
        const data: HalApiResponse = await response.json();

        // Filtrer les entrées pour éliminer les doublons sans identifiant interne AureHAL
        const filteredDocs = data.response.docs.reduce((acc: HalAuthor[], doc): HalAuthor[] => {
          const baseId = doc.docid.split('-')[0];
          const existingDoc = acc.find(d => d.docid.startsWith(baseId));

          if (!existingDoc || (existingDoc && existingDoc.docid.endsWith('-0') && !doc.docid.endsWith('-0'))) {
            if (existingDoc) {
              const index = acc.indexOf(existingDoc);
              acc.splice(index, 1);
            }
            acc.push({
              docid: doc.docid,
              label: doc.label_s || `${doc.firstName_s || ''} ${doc.lastName_s || ''}`,
              firstName: doc.firstName_s,
              lastName: doc.lastName_s
            });
          }

          return acc;
        }, []);

        // Créer les suggestions
        const suggestions = filteredDocs.map((doc): HalAuthor => ({
          docid: doc.docid,
          label: doc.label || `${doc.firstName || ''} ${doc.lastName || ''}`,
          firstName: doc.firstName,
          lastName: doc.lastName
        }));

        // Limiter à 5 suggestions
        halSuggestions.value = suggestions.slice(0, 5);

      } catch (error) {
        console.error('Erreur lors de la recherche HAL:', error);
        halSuggestions.value = [];
      } finally {
        isLoading.value = false;
      }
    }, 500);

    const handleHalSearchInput = (event: Event): void => {
      const input = event.target as HTMLInputElement;
      halSearchQuery.value = input.value;
      showSuggestions.value = true;
      searchHal(input.value);
    };

    const selectAuthor = async (author: HalAuthor): Promise<void> => {
      agentData.first_name = author.firstName || '';
      agentData.last_name = author.lastName || '';
      agentData.idref_id = author.docid;
      showSuggestions.value = false;
      if (author.firstName && author.lastName) {
        selectedAuthorName.value = {
          firstName: author.firstName,
          lastName: author.lastName
        };
        await searchAffiliations(author.firstName, author.lastName);
      }
    };

    const searchAffiliations = async (firstName: string, lastName: string): Promise<void> => {
      isLoadingAffiliations.value = true;
      try {
        const response = await fetch(
          `https://api.archives-ouvertes.fr/search/authorstructure/?firstName_t=${encodeURIComponent(firstName)}&lastName_t=${encodeURIComponent(lastName)}&wt=xml&getParents=false`
        );
        const text = await response.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");

        const orgs = xmlDoc.getElementsByTagName('org');
        const affiliations = Array.from(orgs).map((org): HalAffiliation => {
          const orgNames = Array.from(org.getElementsByTagName('orgName'));
          const mainName = orgNames.find(name => !name.getAttribute('type'))?.textContent || '';
          const acronym = orgNames.find(name => name.getAttribute('type') === 'acronym')?.textContent || '';

          return {
            id: org.getAttribute('xml:id') || '',
            name: mainName,
            acronym: acronym,
            status: org.getAttribute('status') as HalAffiliation['status']
          };
        });

        // Trier les affiliations par status
        affiliationSuggestions.value = affiliations.sort((a, b): number => {
          const statusOrder = { VALID: 1, INCOMING: 2, OLD: 4 };
          const statusA = statusOrder[a.status] || 3;
          const statusB = statusOrder[b.status] || 3;
          return statusA - statusB;
        });
        
        // Afficher automatiquement les suggestions si des affiliations sont trouvées
        if (affiliationSuggestions.value.length > 0) {
          showAffiliationSuggestions.value = true;
        }
      } catch (error) {
        console.error('Erreur lors de la recherche des affiliations:', error);
        affiliationSuggestions.value = [];
      } finally {
        isLoadingAffiliations.value = false;
      }
    };

    const selectAffiliation = (affiliation: HalAffiliation): void => {
      agentData.affiliation = affiliation.acronym || affiliation.name;
      agentData.affiliation_id = affiliation.id;
      affiliationSuggestions.value = [];
      showAffiliationSuggestions.value = false;
    };

    const toggleAffiliationSuggestions = (): void => {
      if (affiliationSuggestions.value.length > 0) {
        showAffiliationSuggestions.value = true;
      }
    };

    const handleAffiliationInput = (event: Event): void => {
      const input = event.target as HTMLInputElement;
      if (input.value.length > 0 && affiliationSuggestions.value.length > 0) {
        showAffiliationSuggestions.value = true;
      } else {
        showAffiliationSuggestions.value = false;
      }
    };

    const refreshAffiliations = async (): Promise<void> => {
      if (selectedAuthorName.value) {
        await searchAffiliations(
          selectedAuthorName.value.firstName,
          selectedAuthorName.value.lastName
        );
        // Afficher automatiquement les suggestions après le rafraîchissement
        if (affiliationSuggestions.value.length > 0) {
          showAffiliationSuggestions.value = true;
        }
      }
    };

    const resetAffiliation = (): void => {
      agentData.affiliation = '';
      agentData.affiliation_id = undefined;
      affiliationSuggestions.value = [];
      showAffiliationSuggestions.value = false;
    };

    // Fonction pour vérifier s'il y a des modifications
    const hasModifications = (): boolean => {
      if (!initialDataSnapshot.value) {
        // En mode création, vérifier si des champs ont été remplis
        return !!(agentData.last_name || agentData.first_name || agentData.idref_id || 
                 agentData.roles || agentData.group || agentData.affiliation || 
                 agentData.affiliation_id || agentData.agent_link);
      }
      
      // En mode édition, comparer avec les données initiales
      const snapshot = initialDataSnapshot.value;
      return agentData.last_name !== snapshot.last_name ||
             agentData.first_name !== snapshot.first_name ||
             agentData.idref_id !== snapshot.idref_id ||
             agentData.roles !== snapshot.roles ||
             agentData.group !== snapshot.group ||
             agentData.affiliation !== snapshot.affiliation ||
             agentData.affiliation_id !== snapshot.affiliation_id ||
             agentData.agent_link !== snapshot.agent_link ||
             agentData.is_legal_entity !== snapshot.is_legal_entity;
    };

    const closeModal = (): void => {
      if (hasModifications()) {
        showConfirmModal.value = true;
      } else {
        resetForm();
        showModal.value = false;
      }
    };

    const confirmClose = (): void => {
      showConfirmModal.value = false;
      resetForm();
      showModal.value = false;
    };

    const cancelClose = (): void => {
      showConfirmModal.value = false;
    };

    const resetForm = (): void => {
      Object.assign(agentData, {
        user_id: props.userId,
        last_name: '',
        first_name: '',
        is_legal_entity: false,
        idref_id: '',
        roles: '',
        group: '',
        affiliation: '',
        affiliation_id: '',
        agent_link: '',
        is_public: true,
      });
      halSearchQuery.value = '';
      halSuggestions.value = [];
      showSuggestions.value = false;
      structureSearchQuery.value = '';
      structureSuggestions.value = [];
      showStructureSuggestions.value = false;
      // Réinitialiser le snapshot
      initialDataSnapshot.value = null;
    };

    const handleSubmit = async (): Promise<void> => {
      // Validation avant soumission
      if (!agentData.is_legal_entity && !agentData.last_name?.trim()) {
        console.error('Erreur de validation: last_name est obligatoire pour une personne physique');
        if (notification.value) {
          notification.value.showNotification('Le nom de famille est obligatoire pour une personne physique', 'error');
        }
        return;
      }

      if (agentData.is_legal_entity && !agentData.affiliation?.trim()) {
        console.error('Erreur de validation: affiliation est obligatoire pour une personne morale');
        if (notification.value) {
          notification.value.showNotification('L\'affiliation est obligatoire pour une personne morale', 'error');
        }
        return;
      }

      try {
        if (props.mode === 'create') {
          const response = await agents.createAgent(agentData);
          emit('agent-created', response);
          if (notification.value) {
            notification.value.showNotification('Agent créé avec succès', 'success');
          }
          resetForm();
          showModal.value = false;
        } else {
          const response = await agents.updateAgent(props.initialData?.uuid || '', agentData);
          emit('agent-updated', response);
          if (notification.value) {
            notification.value.showNotification('Agent mis à jour avec succès', 'success');
          }
          resetForm();
          showModal.value = false;
        }
      } catch (error: any) {
        console.error(`Erreur lors de ${props.mode === 'edit' ? 'la mise à jour' : 'la création'} de l'agent:`, error);
        
        // Vérifier si c'est une erreur 409 (agent existant)
        if (error?.status === 409 && error?.details?.existing_agent) {
          // Convertir l'agent existant en AgentWithMemos (avec un tableau de mémos vide)
          const existingAgent = {
            ...error.details.existing_agent,
            memos: []
          };
          emit('agent-exists', existingAgent);
          // Ne pas afficher de notification d'erreur, le modal s'en chargera
          return;
        }
        
        if (notification.value) {
          notification.value.showNotification('Erreur lors de la création/mise à jour de l\'agent', 'error');
        }
      }
    };

    const closeAuthorSuggestions = (): void => {
      showSuggestions.value = false;
      halSearchQuery.value = '';
      agentData.last_name = '';
      agentData.idref_id = undefined;
    };

    const resetHalSearch = (): void => {
      halSearchQuery.value = '';
      showSuggestions.value = false;
      halSuggestions.value = [];
    };

    const resetNameFields = (): void => {
      agentData.first_name = '';
      agentData.last_name = '';
    };

    const handleStructureSearchInput = debounce(async (event: Event): Promise<void> => {
      const input = event.target as HTMLInputElement;
      structureSearchQuery.value = input.value;

      if (input.value.length < 3) {
        structureSuggestions.value = [];
        showStructureSuggestions.value = false;
        return;
      }

      showStructureSuggestions.value = true;

      try {
        const response = await fetch(`https://api.archives-ouvertes.fr/ref/structure/?q=${encodeURIComponent(input.value)}&wt=json&rows=10&fl=label_s,docid`);
        const data: StructureApiResponse = await response.json();
        structureSuggestions.value = data.response.docs.map((doc): StructureSuggestion => ({
          id: doc.docid,
          name: doc.label_s
        }));
      } catch (error) {
        console.error('Erreur lors de la recherche de structures:', error);
        structureSuggestions.value = [];
      }
    }, 500);

    const selectStructure = (structure: StructureSuggestion): void => {
      agentData.affiliation = structure.name;
      agentData.affiliation_id = structure.id;
      structureSearchQuery.value = structure.name;
      showStructureSuggestions.value = false;
    };

    const resetStructureSearch = (): void => {
      structureSearchQuery.value = '';
      structureSuggestions.value = [];
      showStructureSuggestions.value = false;
      agentData.affiliation = '';
      agentData.affiliation_id = '';
    };

    return {
      showModal,
      agentData,
      halSearchQuery,
      halSuggestions,
      showSuggestions,
      isLoading,
      handleSubmit,
      handleHalSearchInput,
      selectAuthor,
      closeModal,
      closeAuthorSuggestions,
      affiliationSuggestions,
      isLoadingAffiliations,
      selectAffiliation,
      toggleAffiliationSuggestions,
      handleAffiliationInput,
      refreshAffiliations,
      resetAffiliation,
      selectedAuthorName,
      resetHalSearch,
      resetNameFields,
      structureSearchQuery,
      structureSuggestions,
      showStructureSuggestions,
      showAffiliationSuggestions,
      handleStructureSearchInput,
      selectStructure,
      resetStructureSearch,
      halSearchInput,
      structureSearchInput,
      affiliationInput,
      affiliationContainer,
      notification,
      showConfirmModal,
      confirmClose,
      cancelClose,
      handleConfidentialChange: () => {} // Fonction vide pour éviter les erreurs
    };
  }
});
</script>

<style scoped>
/* Ajouter des styles pour aligner la carte avec les autres cartes */
.bg-white {
  height: 100%;
}

/* Forcer les styles pour tous les inputs dans AgentCreator */
input[type="text"],
input[type="email"],
input[type="url"],
input[type="password"],
input[type="number"],
input:not([type]),
textarea,
select {
  margin-bottom: 8px !important;
  border: 1px solid #d1d5db !important;
  /* border-gray-300 */
  border-radius: 6px !important;
  /* rounded-md */
  padding: 8px 12px !important;
  /* px-3 py-2 */
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
  /* shadow-sm */
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
  background-color: white !important;
  width: 100% !important;
  display: block !important;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="url"]:focus,
input[type="password"]:focus,
input[type="number"]:focus,
input:not([type]):focus,
textarea:focus,
select:focus {
  outline: none !important;
  border-color: #3b82f6 !important;
  /* border-blue-500 */
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
  /* ring-blue-500 */
}

input::placeholder,
textarea::placeholder {
  color: #9ca3af !important;
  /* text-gray-400 */
}

/* Labels */
label {
  margin-bottom: 4px !important;
  display: block !important;
  font-weight: 500 !important;
  color: #374151 !important;
  /* text-gray-700 */
  font-size: 0.875rem !important;
  /* text-sm */
}

/* Boutons toggle spéciaux pour le type d'agent */
button[type="button"] {
  margin-bottom: 0 !important;
  padding: 8px 16px !important;
  border: 1px solid #d1d5db !important;
  transition: all 0.15s ease-in-out !important;
}
</style>